import React, { useEffect, useState } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { useFieldArray, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiBox,
  MiButton,
  MiControlledTextField,
  MiModalConfirm,
  MiPlaceholderList,
  MiReorderButton,
  MiSectionHeader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  MODAL_TITLES,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { Box, GridLegacy, useTheme } from "@mui/material";
import { useAccountDrawsTabViewModel } from "./useAccountDrawsTabViewModel";

const schema = z.object({
  draws: z
    .array(
      z.object({
        name: z.string().min(1, "Name is required"),
      })
    )
    .superRefine((items, ctx) => {
      const uniqueValues = new Map();
      items.forEach((item, index) => {
        const firstIndex = uniqueValues.get(item.name);
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Draw name must be unique`,
            path: [index, "name"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Draw name must be unique`,
            path: [firstIndex, "name"],
          });
          return;
        }
        uniqueValues.set(item.name, index);
      });
    }),
  drawTotal: z.number().refine((val) => val === 100, {
    message: "Total must equal 100%",
  }),
});

export function AccountDrawsTab({
  stateManager,
  accountDetail,
  editMode,
  setEditMode,
  getAccountDetails,
}) {
  const { classes } = useStyles();
  const theme = useTheme();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const {
    control,
    formState: { isValid, isDirty },
    reset,
    getValues,
    setValue,
  } = useForm({
    mode: "onChange",
    defaultValues: { draws: [] },
    resolver: zodResolver(schema),
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "draws",
    rules: { minLength: 1 },
  });

  const isRemoved = (index) => getValues(`draws[${index}].removed`);

  const {
    draws,
    getDraws,
    handleSaveDraws,
    handleAddDraw,
    handleRemoveDraw,
    handleEnableEdit,
    isLoading,
    handleEvenDistribution,
    handleCalculateTotal,
  } = useAccountDrawsTabViewModel({
    getAccountDetails,
    stateManager,
    setEditMode,
    isRemoved,
    getValues,
    setValue,
    append,
    remove,
    reset,
  });

  useEffect(() => {
    if (accountDetail?.id) {
      getDraws(accountDetail?.id);
    }
  }, [accountDetail?.id]);

  return (
    <>
      <MiBox className={classes.root}>
        <Box className={classes.leftContainer}>
          <MiSectionHeader
            title="Draw Schedule Setup"
            subtitle={`Enter the Draw schedule for ${accountDetail?.name}.`}
            leftIcon={mdi[ICONS_WEB.DOCUMENT]}
            rightIcon={!editMode && mdi[ICONS_WEB.PENCIL_OUTLINE]}
            onRightIconClick={handleEnableEdit}
          />
          {!editMode && draws.length === 0 && !isLoading && (
            <MiPlaceholderList
              icon={mdi[ICONS_WEB.BULLET_LIST]}
              text="No draws set up for this customer."
              helperText="To create, begin editing and click + Add Draw button."
            />
          )}
          <Box className={classes.scrollable}>
            <Flipper flipKey={fields.map((draw) => draw.id).join("-")}>
              <GridLegacy
                container
                alignItems="center"
                spacing={2}
                rowSpacing={3}
              >
                <GridLegacy item xs={6} />
                <GridLegacy item xs={3}>
                  <Box style={{ visibility: !editMode && "hidden" }}>
                    <MiButton
                      title="Distribute Evenly"
                      variant={BUTTON_VARIANTS.OUTLINED}
                      onClick={handleEvenDistribution}
                      style={{ width: "100%" }}
                    />
                  </Box>
                </GridLegacy>
                <GridLegacy item xs={3} />
                {fields.map((field, index) => (
                  <Flipped key={field.id} flipId={field.id}>
                    <GridLegacy
                      container
                      item={12}
                      key={field.id}
                      spacing={2}
                      sx={{ alignItems: "center" }}
                    >
                      <GridLegacy item xs={6}>
                        <MiControlledTextField
                          label={`Draw #${index + 1}`}
                          name={`draws[${index}].name`}
                          required={editMode}
                          disabled={isLoading || isRemoved(index) || !editMode}
                          readOnly={!editMode}
                          formControl={control}
                          placeholder={`Enter Draw #${index + 1} name`}
                          inputProps={{ maxLength: 32 }}
                          sx={{
                            background:
                              isRemoved(index) && theme.palette.red.light,
                          }}
                        />
                      </GridLegacy>
                      <GridLegacy item xs={3}>
                        <NumericFormat
                          customInput={MiControlledTextField}
                          thousandSeparator
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          allowLeadingZeros={false}
                          suffix="%"
                          name={`draws[${index}].percentage`}
                          disabled={isLoading || isRemoved(index) || !editMode}
                          formControl={control}
                          required={editMode}
                          label="%"
                          readOnly={!editMode}
                          value={getValues(`draws[${index}].percentage`)}
                          sx={{
                            background:
                              isRemoved(index) && theme.palette.red.light,
                          }}
                          onValueChange={({ floatValue }) => {
                            setValue(`draws[${index}].percentage`, floatValue, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            handleCalculateTotal(getValues("draws"));
                          }}
                        />
                      </GridLegacy>
                      <GridLegacy item xs={1} className={classes.reorderBtn}>
                        {editMode && (
                          <MiReorderButton
                            index={index}
                            length={getValues("draws").length}
                            onMoveUp={() => {
                              move(index, index - 1);
                              setValue(
                                `draws[${index}].displayOrder`,
                                index - 1
                              );
                            }}
                            onMoveDown={() => {
                              move(index, index + 1);
                              setValue(
                                `draws[${index}].displayOrder`,
                                index + 1
                              );
                            }}
                            disabled={isLoading}
                            disableUp={index === 0}
                            disableDown={
                              index === getValues("draws").length - 1
                            }
                            disableRipple
                          />
                        )}
                      </GridLegacy>
                      <GridLegacy item xs={1}>
                        {editMode && (
                          <MiButton
                            startIcon={{
                              path: isRemoved(index)
                                ? mdi[ICONS_WEB.ARROW_U_LEFT_TOP]
                                : mdi[ICONS_WEB.MINUS],
                            }}
                            color={
                              isRemoved(index)
                                ? BUTTON_COLORS.PRIMARY
                                : BUTTON_COLORS.RED
                            }
                            variant={BUTTON_VARIANTS.CONTAINED}
                            className={classes.addDelBtn}
                            disabled={
                              isLoading ||
                              (index === 0 && getValues("draws").length <= 1)
                            }
                            onClick={() => handleRemoveDraw(index)}
                          />
                        )}
                      </GridLegacy>
                    </GridLegacy>
                  </Flipped>
                ))}
                <GridLegacy item xs={6}>
                  {editMode && (
                    <MiButton
                      title="Add Draw"
                      variant={BUTTON_VARIANTS.OUTLINED}
                      color={BUTTON_COLORS.PRIMARY}
                      disabled={!editMode}
                      startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
                      onClick={handleAddDraw}
                      sx={{ width: 120 }}
                    />
                  )}
                </GridLegacy>
                {getValues("draws").length > 0 && (
                  <GridLegacy item xs={3}>
                    <NumericFormat
                      customInput={MiControlledTextField}
                      formControl={control}
                      name="drawTotal"
                      label="Total %"
                      value={getValues("drawTotal")}
                      thousandSeparator
                      decimalScale={2}
                      allowNegative={false}
                      suffix="%"
                      readOnly
                      disabled
                    />
                  </GridLegacy>
                )}
              </GridLegacy>
            </Flipper>
          </Box>
        </Box>
      </MiBox>
      <Footer
        show={editMode}
        saveTitle={MODAL_TITLES.SAVE}
        isDirty={isDirty}
        handleSaveClick={() => {
          if (draws.length > 0) {
            setOpenConfirmModal(true);
            return;
          }
          handleSaveDraws(getValues("draws"), accountDetail?.id);
        }}
        handleCancelClick={() => {
          setEditMode(false);
          reset({ draws });
        }}
        disableSave={
          !getValues("draws").every((_, index) => isRemoved(index)) &&
          (!isValid || isLoading || !isDirty)
        }
      />
      <MiModalConfirm
        title={MODAL_TITLES.SAVE}
        textPositive="Confirm"
        open={openConfirmModal}
        handleNegative={() => setOpenConfirmModal(false)}
        handlePositive={() => {
          handleSaveDraws(getValues("draws"), accountDetail?.id);
          setOpenConfirmModal(false);
        }}
        description={`Are you sure you want to modify the Draw Schedule Setup for ${accountDetail?.name}?`}
        buttonPositiveColor={BUTTON_COLORS.PRIMARY}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
  },
  leftContainer: {
    width: "50%",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  addDelBtn: {
    borderRadius: "50%",
    padding: theme.spacing(0),
  },
  reorderBtn: {
    display: "flex",
    justifyContent: "center",
    "& > *": {
      width: "50%",
    },
  },
  scrollable: {
    overflowY: "auto",
    flex: 1,
    minWidth: 305,
  },
}));
